import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { AuthError } from "@/api/Api";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatChatDate = (date: Date): string => {
  return format(date, "EEE h:mm a");
};

/**
 * Retry a function with exponential backoff
 * @param fn Function to retry
 * @param maxRetries Maximum number of retries
 * @param baseDelayMs Base delay in milliseconds
 * // dont retry on authentication errors
 * @returns Result of the function
 */
export async function withRetry<T>(
  fn: () => Promise<T | undefined>,
  maxRetries: number = 3,
  baseDelayMs: number = 500,
  maxDelayMs: number = 10_000
): Promise<T | undefined> {
  let attempt = 0;

  while (true) {
    try {
      return await fn();
    } catch (error) {
      if (error instanceof AuthError) {
        // stop retrying its assumed that auth error
        // is not recoverable and always go to login screen at API layer
        return undefined;
      }
      attempt++;

      // If we've reached max retries, throw the error
      if (attempt >= maxRetries) {
        throw error;
      }
      // Calculate delay with exponential backoff: baseDelay * 2^attempt
      let delay = baseDelayMs * Math.pow(2, attempt - 1);
      if (delay > maxDelayMs) {
        delay = maxDelayMs;
      }
      // Add some jitter to prevent all retries happening at exactly the same time
      const jitter = Math.random() * 100;
      // Wait for the calculated delay
      await new Promise((resolve) => setTimeout(resolve, delay + jitter));
      // Loop will continue to the next retry
    }
  }
}

// Shuffle an array using the Fisher-Yates algorithm
export function shuffleArray<T>(array: T[]): T[] {
  const result = [...array]; // copy the array to avoid mutating the original
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1)); // random index from 0 to i
    [result[i], result[j]] = [result[j], result[i]]; // swap elements
  }
  return result;
}
